import type { PayrollRun } from "@shared/types/payroll";

export type RpcResult<T> = {
  data: T | null;
  error: { message?: string; error_description?: string; hint?: string } | null;
};

export type CompanyPayrollPeriod = {
  id: string;
  company_id: string;
  period_type: string;
  period_start: string;
  period_end: string;
  period_label: string;
  created_at: string;
  created_by: string | null;
  evaluation_group_key?: string | null;
  pay_date?: string | null;
  has_payroll_run?: boolean;
  latest_run_id?: string | null;
  latest_run_state?: "DRAFT" | "SUBMITTED" | "ISSUED" | null;
  has_issued_run?: boolean;
  latest_issued_at?: string | null;
  latest_return_note?: string | null;
  latest_return_count?: number | null;
  latest_run_is_returned_to_clerk?: boolean;
  has_time_data?: boolean;
  period_status?: string | null;
};

export type PreflightEmployeeBlocker = {
  requirement_key: string;
  label: string | null;
  reason: string | null;
  editable_by_payroll_clerk: boolean;
  edit_target_kind: string | null;
  edit_target_key: string | null;
};

export type PreflightAffectedEmployeeBlocker = {
  employee_profile_id: string;
  employee_number_mode: string | null;
  can_edit_employee_number: boolean;
  blockers: PreflightEmployeeBlocker[];
};

export type PreflightDetails = {
  affectedEmployeeBlockers: PreflightAffectedEmployeeBlocker[];
};

export type TimesheetRow = {
  user_id: string;
  company_id: string;
  work_date: string;
  full_name: string;
  source_type: string;
  has_shadowing_anomaly: boolean;
  integrity_alert: boolean;
  raw_hours: number;
  daily_reg: number;
  daily_ot: number;
  weekly_ot_carry: number;
  project_id?: string | null;
  log_id?: string | null;
  is_public_holiday?: boolean | null;
  status?: string | null;
};

export type TimesheetSummary = {
  totalDays: number;
  employees: number;
  alerts: number;
  openDisputes: number;
  unmappedLogs: number;
};

export type TimesheetDataset = {
  rows: TimesheetRow[];
  summary: TimesheetSummary;
};

export type EmployeeDisputeRow = {
  id: string;
  company_id: string;
  employee_id: string;
  employee_full_name: string | null;
  employee_custom_id: string | null;
  work_date: string | null;
  proposed_hours: number | null;
  reason: string | null;
  status: "open" | "resolved" | "dismissed" | string;
  created_at: string;
  updated_at?: string | null;
  payload?: unknown;
  resolution_note?: string | null;
};

export type IssuedPayrollRun = {
  id: string;
  company_id: string;
  period_start: string;
  period_end: string;
  issued_at: string;
  issued_by: string | null;
  total_gross: number | null;
  total_net: number | null;
  currency: string | null;
  status: string;
};

export type IssuerQueueRun = {
  id: string;
  company_id: string;
  period_id: string;
  period_type: string | null;
  period_label: string | null;
  evaluation_group_key: string | null;
  status: "DRAFT" | "SUBMITTED" | "ISSUED";
  state: "DRAFT" | "SUBMITTED" | "ISSUED";
  consolidated_id: string | null;
  submitted_at: string | null;
  submitted_by: string | null;
  issued_at: string | null;
  issued_by: string | null;
  return_count: number;
  return_note: string | null;
  last_return_reason: string | null;
  last_return_note: string | null;
  last_returned_at: string | null;
  last_returned_by: string | null;
  employee_count: number | null;
  created_at: string;
};

export type PayrollResolutionRoute =
  | "submitter_actionable"
  | "authorized_resolver_required"
  | "issuer_hold"
  | "system_action_required";

export type PayrollResolutionAssignmentStatus =
  | "assigned"
  | "accepted"
  | "resolved"
  | "cancelled";

export type PayrollResolverRoleSnapshot = {
  user_id: string;
  company_id: string;
  is_owner: boolean;
  is_owner_or_creator: boolean;
  has_submit_credential: boolean;
  has_issue_credential: boolean;
  may_edit_compensation: boolean;
  may_edit_employee_number: boolean;
  captured_at: string;
};

export type PayrollAuthoritySnapshot = Record<string, unknown>;

export type PayrollRunResolutionAssignment = {
  assignment_id: string | null;
  assignment_status: PayrollResolutionAssignmentStatus | null;
  assigned_resolver_user_id: string | null;
  assigned_by: string | null;
  assignment_reason: string | null;
  assigned_at: string | null;
  accepted_at: string | null;
  assignment_resolved_at: string | null;
  cancelled_at: string | null;
};

export type PayrollRunReturnItem = {
  id: string;
  run_id: string;
  company_id: string;
  reason: string;
  severity: string;
  employee_profile_id: string | null;
  field_key: string | null;
  correction_note: string;
  status: string;
  created_at: string;
  created_by: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_route: PayrollResolutionRoute;
  resolver_auth_user_id: string | null;
  resolver_role_snapshot: PayrollResolverRoleSnapshot | null;
  resolved_via_override: boolean;
  override_actor_id: string | null;
  override_session_actor_id: string | null;
  override_reason: string | null;
  authority_snapshot: PayrollAuthoritySnapshot | null;
  assignment: PayrollRunResolutionAssignment | null;
};

export type PendingEmployeeFieldVerificationRow = {
  pending_value_id: string;
  company_id: string;
  employee_id: string | null;
  employee_display_name: string | null;
  employee_number: string | null;
  field_key: string;
  field_label: string;
  field_category: string | null;
  submitted_value: unknown;
  submitted_by: string | null;
  submitted_by_email: string | null;
  submitted_at: string;
  required_authority: string | null;
  import_batch_id: string | null;
  import_row_id: string | null;
  import_row_number: number | null;
  original_filename: string | null;
  diagnostic_payload: unknown;
};

export type VerifyPendingEmployeeFieldValueInput = {
  pendingValueId: string;
  approve: boolean;
  rejectionReason?: string | null;
};

export type VerifyPendingEmployeeFieldValueResult = {
  ok?: boolean;
  code?: string;
  message?: string;
  [key: string]: unknown;
};

export type EmployeeIssuanceReadinessRow = {
  employee_id: string;
  company_id: string;
  field_id: string;
  field_key: string;
  label: string;
  category: string;
  data_type: string;
  metadata_requirement_tier: string;
  required_for_issuance: boolean;
  blocks_issuance: boolean;
  required_for_statutory_compliance: boolean;
  jurisdiction_required: boolean;
  company_enabled: boolean;
  is_sensitive: boolean;
  is_system_derived: boolean;
  resolved_value: unknown;
  is_missing: boolean;
  reachable_by_owner: boolean;
  reachable_by_submitter: boolean;
  reachable_by_issuer: boolean;
  reachable_by_company_actor: boolean;
  blocks_issuance_effective: boolean;
  requires_external_resolution: boolean;
  edit_target_kind: string | null;
  edit_target_key: string | null;
  block_reason: string | null;
  resolution_route:
    | "none"
    | "return_to_submitter"
    | "route_to_authorized_company_actor"
    | "external_resolution_required";
};

export type IssuerQueuePeriod = {
  id: string;
  period_start: string;
  period_end: string;
  period_label: string;
  period_type: string;
  generation_source: string;
  pay_date: string | null;
  evaluation_group_key: string | null;
};

export type PayrollNotification = {
  id: string;
  kind: string;
  payload: any;
  created_at: string;
  read_at: string | null;
};

export type PayrollRunAggregate = {
  gross_total: number;
  net_total: number;
  employer_total: number;
  employee_count: number;
};

export type IssuePayrollRunResponse = {
  bank_transmission_id: string;
  journal_entry_id: string;
};

export type IssuePayrollRunBankingMode = "NACHA" | "CSV" | "API";

export type EmployeeProvisioningResult =
  | {
      ok: true;
      code: string;
      message: string;
      company_id: string;
      employee_profile_id: string;
      created: boolean;
    }
  | {
      ok: false;
      code: string;
      message: string;
    };

export type EmployeeProfileSummary = {
  full_name: string | null;
  custom_employee_id: string | null;
  internal_employee_sequence_number: number | null;
};

export type EmployeeWorkspacePayslipRow = {
  id: string;
  run_id: string;
  employee_id: string;
  issued_at: string | null;
  payload: any;
  payroll_runs?: {
    id: string;
    status: "DRAFT" | "SUBMITTED" | "ISSUED" | string;
    issued_at: string | null;
    period_id: string;
    evaluation_group_key: string;
    payroll_periods?: {
      id?: string;
      period_start: string;
      period_end: string;
      pay_date: string;
    } | null;
  } | null;
};

export type EmployeeSelfServiceTimesheetEntry = {
  log_id: string;
  project_id: string | null;
  work_date: string;
  entry_time: string | null;
  exit_time: string | null;
  total_hours: number | null;
  source_type: string | null;
  is_public_holiday: boolean | null;
  status: string | null;
  has_shadowing_anomaly: boolean | null;
  daily_ot: number | null;
  daily_reg: number | null;
  week_start_date: string | null;
  weekly_ot_carry: number | null;
  integrity_alert: boolean | null;
};

export type EmployeeSelfServiceTimeDispute = {
  id: string;
  work_date: string;
  proposed_hours: number;
  reason: string;
  status: string | null;
  manager_feedback: string | null;
  created_at: string | null;
};

export type EmployeeSelfServiceAbsence = {
  id: string;
  type: string | null;
  start_date: string;
  end_date: string;
  status: string | null;
  certification_state: string | null;
  physical_original_required: boolean;
  physical_original_status: string;
  employee_note: string | null;
  manager_feedback: string | null;
  created_at: string | null;
};

export type EmployeeSelfServiceLeaveRequest = {
  id: string;
  type: string | null;
  start_date: string;
  end_date: string;
  status: string | null;
  employee_note: string | null;
  manager_feedback: string | null;
  created_at: string | null;
};

export type CompanyEmployeeRosterRow = {
  id: string;
  company_id: string;
  auth_user_id: string | null;
  email: string | null;
  custom_employee_id: string | null;
  full_name: string | null;
  first_name: string | null;
  middle_names: string | null;
  last_name: string | null;
  is_active: boolean | null;
  is_payroll_eligible: boolean | null;
  is_management: boolean | null;
};

export type CompanyTimeAwayQueueRow = {
  id: string;
  employee_id: string;
  employee_name: string | null;
  employee_custom_id: string | null;
  type: string | null;
  start_date: string | null;
  end_date: string | null;
  status: string | null;
  certification_state: string | null;
  physical_original_required: boolean | null;
  physical_original_status: string | null;
  employee_note: string | null;
  manager_feedback: string | null;
  created_at: string | null;
  requires_approval: boolean | null;
  can_current_user_approve: boolean | null;
};

export type ApproveCompanyTimeAwayRequestInput = {
  companyId: string;
  absenceId: string;
  managerFeedback?: string | null;
};

export type RejectCompanyTimeAwayRequestInput = {
  companyId: string;
  absenceId: string;
  managerFeedback: string;
};

export function asErrorMessage(error: unknown): string {
  if (!error) return "Unknown error";
  if (typeof error === "string") return error;

  const anyError = error as {
    message?: string;
    error_description?: string;
    hint?: string;
  };

  return (
    anyError?.message ||
    anyError?.error_description ||
    anyError?.hint ||
    JSON.stringify(error)
  );
}

export function assertOk<T>(res: RpcResult<T>): T {
  if (res.error) throw new Error(asErrorMessage(res.error));
  if (res.data === null || res.data === undefined) {
    throw new Error("No data returned");
  }
  return res.data;
}

export function toNumber(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function toBoolean(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.toLowerCase() === "true";
  return false;
}

export function toNullableString(value: unknown): string | null {
  return value == null ? null : String(value);
}

export function normalizePayrollRun(row: any): PayrollRun {
  const state = String(row?.state ?? row?.status ?? "DRAFT") as
    | "DRAFT"
    | "SUBMITTED"
    | "ISSUED";

  return {
    ...row,
    id: String(row.id),
    company_id: String(row.company_id),
    period_id: String(row.period_id),
    period_type: row.period_type == null ? null : String(row.period_type),
    period_label: row.period_label == null ? null : String(row.period_label),
    evaluation_group_key:
      row.evaluation_group_key == null
        ? "DEFAULT"
        : String(row.evaluation_group_key),
    status: state,
    state,
    return_count: toNumber(row.return_count),
    return_note: row.return_note == null ? null : String(row.return_note),
    last_return_reason:
      row.last_return_reason == null ? null : String(row.last_return_reason),
    last_return_note:
      row.last_return_note == null
        ? row.return_note == null
          ? null
          : String(row.return_note)
        : String(row.last_return_note),
    last_returned_at:
      row.last_returned_at == null
        ? null
        : String(row.last_returned_at),
    last_returned_by:
      row.last_returned_by == null
        ? null
        : String(row.last_returned_by),
    submitted_at:
      row.submitted_at == null
        ? null
        : String(row.submitted_at),
    submitted_by:
      row.submitted_by == null
        ? null
        : String(row.submitted_by),
    issued_at:
      row.issued_at == null
        ? null
        : String(row.issued_at),
    issued_by:
      row.issued_by == null
        ? null
        : String(row.issued_by),
    created_at:
      row.created_at == null
        ? ""
        : String(row.created_at),
  } as PayrollRun;
}